import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { STATIC_PATH } from "../../constants/config";
import {
  Icons,
  PlayerContainer,
  PlayerControls,
  Seek,
  SeekBar,
  PlayTimer,
  VolumeContainer,
  VolumeSlider,
} from "../../animo-player/animo-component";
import {
  playPausePlayer,
  setAnimoFullScreen,
  setGlobalVolume,
  setPlayAll,
  setPlayHeadTime,
  showFeedbackModal,
} from "../../redux/actions/appUtils";
import { secondsToMinute } from "../timeline/timeline-helper";
import ActionTypes from "../../constants/action-types";
import Slider from "../zoom/zoom-slider";
import { Duration, PlayButton } from "./shorts-components";
import contextActionType from "./context-action-type";
import { useShortsContext } from "./shortsContextApi";
import isIphoneSafari from "../../helper/isIphoneSafari";

const {
  GET_STYLES_OPEN,
  SET_STYLES_OPEN,
  SET_ACTIVE_PLAYER,
  GET_PRESET_MODAL_OPEN,
} = contextActionType;

const ShortsPlayerControlPanel = ({ startTime, endTime, videoRef, isShorts }) => {
  const t1Ref = useRef(null);

  const appReducer = useSelector((state) => state.app);
  const isPlayAll = appReducer.get("isPlayAll");
  const t1 = appReducer.get("t1");
  const pausePlayer = appReducer.get("isPausePlayer");
  const animoPlayerId = appReducer.get("animoPlayerId");
  const projectDuration = useSelector((state) => state.projectDetails.get("duration"));
  const duration = endTime ? endTime - startTime : projectDuration;
  const isPlayerLoaded = useSelector((state) => state.app.get("isLoaded") || state.app.get("isAnimLoaded"));
  const dispatch = useDispatch();
  const seekBarRef = useRef();
  const playSeekTimer = useRef();
  const currentTime = useRef();

  const [percentage, setPercentage] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isPausePlayer, setIsPausePlayer] = useState(false);
  const [isShowControl, setIsShowControl] = useState(false);
  const [isIosSafari, setIsIosSafari] = useState(false);
  const [showPlayPauseIcon, setShowPlayPauseIcon] = useState(false);
  const playPauseIconTimer = useRef(null);
  const prevPresetOpenRef = useRef(false);
  const isManuallyPausedRef = useRef(false);
  const { getShortsContext, setShortsContext } = useShortsContext();
  const isPlayPause = getShortsContext(GET_STYLES_OPEN);
  const presetModalOpen = getShortsContext(GET_PRESET_MODAL_OPEN);

  useEffect(() => {
    // Only initialize if startTime is available
    if (startTime !== undefined && startTime !== null) {
      // Setting playhead to play start of a shorts.
      dispatch(setPlayHeadTime({ playhead: startTime }));
      // Initialize currentTime display with start time
      currentTime.current = secondsToMinute(0);
      setPercentage(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startTime]);

  useEffect(() => {
    // Show icon when hovering for 2 seconds
    if (isShowControl && isShorts) {
      setShowPlayPauseIcon(true);
      if (playPauseIconTimer.current) {
        clearTimeout(playPauseIconTimer.current);
      }
      // Hide icon after 2 seconds on hover (only if video is playing)
      if (isPlayAll && !isPausePlayer) {
        playPauseIconTimer.current = setTimeout(() => {
          setShowPlayPauseIcon(false);
        }, 2000);
      }
    } else if (!isShowControl) {
      // Hide icon when not hovering
      setShowPlayPauseIcon(false);
      if (playPauseIconTimer.current) {
        clearTimeout(playPauseIconTimer.current);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShowControl, isPlayAll, isPausePlayer]);


  useEffect(() => {
    // Keep the ref updated with the latest value of t1 for pausing the player on tab visibilitychange.
    t1Ref.current = t1;
  }, [t1]);

  /**
  * Function to pause the player on moving to other tab.
  */
  const handleVisibilityChange = () => {
    if (document.hidden) {
      let playhead = startTime;
      if (t1Ref.current && typeof t1Ref.current.time === 'function') {
        playhead = t1Ref.current.time();
      }
      dispatch(playPausePlayer({ playhead, isPause: true }));
    }
  }

  useEffect(() => {
    setIsIosSafari(isIphoneSafari());
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsPausePlayer(pausePlayer);
  }, [pausePlayer]);

  useEffect(() => {
    if (isPlayAll && !isPausePlayer && startTime !== undefined && startTime !== null) {
      playSeekTimer.current = setInterval(() => {
        // use recent props for interval callback
        // Ensure t1 is available before calling time()
        if (t1 && typeof t1.time === 'function') {
          const curTime = t1.time() - startTime;
          let percent;
          if (curTime >= 0) {
            currentTime.current = secondsToMinute(curTime);
            percent = (curTime / duration) * 100;
            setPercentage(percent);
          }
          if (percent >= 99.9) {
            // pausing and resetting the playhead to start on play end.
            setTimeout(() => {
              dispatch(playPausePlayer({ playhead: startTime, isPause: false }));
              setPercentage(0);
            }, 300);
          }
        }
      }, 1000 / 30);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlayAll, isPausePlayer, startTime, t1]);

  useEffect(() => {
    if (isPausePlayer) {
      clearInterval(playSeekTimer.current);
    }
  }, [isPausePlayer]);

  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
      dispatch({
        type: ActionTypes.SET_RESIZE,
      });
    };
    document.addEventListener("fullscreenchange", handleFullScreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullScreenChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePlayPause = () => {
    // Don't allow playback if startTime is not available
    if (startTime === undefined || startTime === null) {
      return;
    }

    // Show play/pause icon for 2 seconds
    if (isShorts) {
      setShowPlayPauseIcon(true);
      if (playPauseIconTimer.current) {
        clearTimeout(playPauseIconTimer.current);
      }
      playPauseIconTimer.current = setTimeout(() => {
        setShowPlayPauseIcon(false);
      }, 2000);
    }

    if (videoRef?.current && !videoRef.current.paused) {
      videoRef.current.pause();
      videoRef.current.currentTime = startTime;
      videoRef.current.style.display = "none";
      setShortsContext(SET_ACTIVE_PLAYER, null);
    }

    // Safely get playhead with fallback to startTime if t1 is not available
    let playhead = startTime;
    if (t1 && typeof t1.time === 'function') {
      playhead = t1.time();
    }

    if (playhead === endTime) {
      dispatch(playPausePlayer({ playhead: startTime, isPause: false }));
      setShortsContext(SET_ACTIVE_PLAYER, animoPlayerId);
      dispatch(showFeedbackModal({ show: false, view: false }));
      isManuallyPausedRef.current = false;
      return;
    }

    if (!isPlayAll && isPausePlayer) {
      // Set global playhead to startTime before starting playback
      dispatch(setPlayHeadTime({ playhead: startTime }));
      dispatch(playPausePlayer({ playhead: startTime, isPause: false }));
      dispatch(setPlayAll(true, false));
      setShortsContext(SET_ACTIVE_PLAYER, animoPlayerId);
      isManuallyPausedRef.current = false;
    } else if (!isPlayAll) {
      // Initial play - ensure timeline starts from startTime
      dispatch(setPlayHeadTime({ playhead: startTime }));
      dispatch(setPlayAll(true, false));
      setShortsContext(SET_ACTIVE_PLAYER, animoPlayerId);
      isManuallyPausedRef.current = false;
    } else if (isPausePlayer && isPlayAll) {
      // Play after pause
      dispatch(playPausePlayer({ playhead, isPause: false }));
      setShortsContext(SET_ACTIVE_PLAYER, animoPlayerId);
      dispatch(showFeedbackModal({ show: false, view: false }));
      isManuallyPausedRef.current = false;
    } else if (!isPausePlayer && isPlayAll) {
      // Pause
      dispatch(playPausePlayer({ playhead, isPause: true }));
      setShortsContext(SET_ACTIVE_PLAYER, null);
      dispatch(showFeedbackModal({ show: true, view: false }));
      isManuallyPausedRef.current = true;
    }
  };

  const seekHandler = (e) => {
    const seekBound = seekBarRef.current?.getBoundingClientRect();
    const seekPercentage = (e.pageX - seekBound.x) / seekBound.width;
    const playhead = seekPercentage * duration;
    setPercentage((playhead / duration) * 100);
    currentTime.current = secondsToMinute(playhead);
    dispatch(setPlayHeadTime({ playhead: playhead + startTime }));
  };

  const handleFullScreen = (fullScreen) => {
    setIsFullScreen(fullScreen);
    dispatch(setAnimoFullScreen({ isFullScreen: fullScreen }));
  };

  const fullScreenHandler = () => {
    let playhead = startTime;
    if (t1 && typeof t1.time === 'function') {
      playhead = t1.time();
    }
    dispatch(playPausePlayer({ playhead, isPause: true }));
    if (!document.fullscreenElement) {
      document.getElementById(animoPlayerId).requestFullscreen();
      handleFullScreen(true);
    } else {
      document.exitFullscreen();
      handleFullScreen(false);
    }
  };

  useEffect(() => {
    if (isPlayPause) {
      let playhead = startTime;
      if (t1 && typeof t1.time === 'function') {
        playhead = t1.time();
      }
      dispatch(playPausePlayer({ playhead, isPause: true }));
      setShortsContext(SET_STYLES_OPEN, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlayPause, t1, startTime]);

  useEffect(() => {
    const wasPresetOpen = prevPresetOpenRef.current;
    prevPresetOpenRef.current = presetModalOpen;

    if (!wasPresetOpen || presetModalOpen || !isShorts) {
      return;
    }

    if (isManuallyPausedRef.current) {
      return;
    }

    let playhead = startTime;
    if (t1 && typeof t1.time === "function") {
      playhead = t1.time();
    }

    dispatch(playPausePlayer({ playhead, isPause: false }));
    dispatch(setPlayAll(true, false));
    setShortsContext(SET_ACTIVE_PLAYER, animoPlayerId);

    if (videoRef?.current) {
      videoRef.current.style.display = "block";
      videoRef.current.play().catch(() => { });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetModalOpen, isShorts, startTime, t1, animoPlayerId]);

  useEffect(() => {
    if (percentage >= 99) {
      dispatch(showFeedbackModal({ show: true, view: false }));
    }
  }, [percentage, dispatch]);

  const changeVolumeHandler = (e) => {
    setVolume(e.target.value);
    dispatch(setGlobalVolume({ globalVolume: Number(e.target.value) }));
    let playhead = startTime;
    if (t1 && typeof t1.time === 'function') {
      playhead = t1.time();
    }
    dispatch(setPlayHeadTime({ playhead }));
  };

  const muteHandler = () => {
    let globalVolume;
    if (volume) {
      setVolume(0);
      globalVolume = 0;
    } else {
      setVolume(1);
      globalVolume = 1;
    }
    dispatch(setGlobalVolume({ globalVolume }));
    // Seek the player to update the current volume.
    let playhead = startTime;
    if (t1 && typeof t1.time === 'function') {
      playhead = t1.time();
    }
    dispatch(setPlayHeadTime({ playhead }));
  };

  const mouseEnterHandler = () => {
    setIsShowControl(true);
    if (!isPlayAll && videoRef) {
      videoRef.current.style.display = "block";
      videoRef.current.play().catch(() => { });
    }
  };

  const mouseLeaveHandler = () => {
    setIsShowControl(false);
    if (videoRef?.current && !isPlayAll) {
      videoRef.current.pause();
      videoRef.current.currentTime = startTime;
      videoRef.current.style.display = "none";
    }
  };

  const volumeIcon =
    volume === 0 ? `mute` : volume <= 0.49 ? `low-volume` : `high-volume`;

  return (
    <>
      <PlayerControls
        onClick={handlePlayPause}
        onMouseEnter={mouseEnterHandler}
        onMouseLeave={mouseLeaveHandler}
      >


        <>
          {/* Initial play icon */}
          {!isPlayAll && (
            <PlayButton onClick={handlePlayPause}>
              <Icons
                src={`${STATIC_PATH}shorts-play.svg`}
                alt="play"
                width="64px"
                height="64px"
                mobileWidth="48px"
                mobileHeight="48px"
              />
            </PlayButton>
          )}

          {/* Pause icon only after player loaded */}
          {isPlayAll &&
            isPlayerLoaded &&
            showPlayPauseIcon && (
              <PlayButton onClick={handlePlayPause}>
                <Icons
                  src={`${STATIC_PATH}${!isPausePlayer
                    ? "shorts-pause"
                    : "shorts-play"
                    }.svg`}
                  alt={
                    !isPausePlayer
                      ? "pause"
                      : "play"
                  }
                  width="64px"
                  height="64px"
                  mobileWidth="48px"
                  mobileHeight="48px"
                />
              </PlayButton>
            )}

          {isShorts && (<Duration
            right="auto"
            left="8px"
            style={{
              width: "fit-content",
              padding: "0px 6px",
            }}
          >
            {currentTime.current || secondsToMinute(0)} /{" "}
            {secondsToMinute(duration)}
          </Duration>)}
        </>
      </PlayerControls>
      {isPlayAll && (
        <PlayerContainer
          height={isShorts ? "6px" : "32px"}
          width={isShorts ? "100%" : "98%"}
          gap={'8px'}
          display={isShowControl ? "flex" : "none"}
          isShorts={isShorts}
          onMouseEnter={() => setIsShowControl(true)}
        >
          {!isShorts && <Icons
            height="22px"
            width="22px"
            src={`${STATIC_PATH}${isPlayAll && !isPausePlayer ? "animo-pause" : "play-icon"}.svg`}
            id="playIcon"
            alt=""
            onClick={handlePlayPause}
          />}
          {!isShorts && <PlayTimer>
            {currentTime.current || secondsToMinute(duration)}
          </PlayTimer>}
          <SeekBar
            ref={seekBarRef}
            onClick={seekHandler}
            isFullScreen={isFullScreen}
          >
            <Seek width={`${percentage}%`} />
          </SeekBar>
          {!isShorts && <VolumeContainer>
            <Icons
              height="22px"
              width="22px"
              src={`${STATIC_PATH}animo-${volumeIcon}.svg`}
              alt="animo-volume"
              onClick={muteHandler}
            />
            <VolumeSlider className="animo-volume-tool-bar">
              <Slider
                width={"64px"}
                height={"4px"}
                border={"1px solid #DADADC"}
                borderRadius={"2px"}
                background={"#DADADC"}
                margin={"0"}
                thumb={{
                  background: "#637191 0% 0% no-repeat padding-box",
                  border: "none",
                  height: "10px",
                  width: "10px",
                  hoverBG: "#00A7E5",
                  hoverBorder: "1.7px solid #637191",
                }}
                onChangeSliderValue={() => { }}
                onMouseUp={changeVolumeHandler}
                value={volume}
                min={0}
                max={1}
                inputMin={0}
                inputMax={1}
                step={0.01}
                progressBackground={`#00A7E5`}
                isChangeBg={true}
              />
            </VolumeSlider>
          </VolumeContainer>}
          {!isShorts && !isIosSafari && (
            <Icons
              height="22px"
              width="22px"
              src={`${STATIC_PATH}fullscreen${isFullScreen ? "-exit" : ""}.svg`}
              alt=""
              onClick={fullScreenHandler}
            />
          )}
        </PlayerContainer>
      )}
    </>
  );
};

ShortsPlayerControlPanel.propTypes = {
  startTime: PropTypes.number,
  endTime: PropTypes.number,
  videoRef: PropTypes.object,
  isShorts: PropTypes.bool,
};

export default ShortsPlayerControlPanel;